import { useTranslation } from '../i18n';

import React, { useState } from 'react';
import { Modal, Form, Input, Typography, Alert, Upload, Button, Divider, Table } from 'antd';
import { UploadOutlined, FileExcelOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import { looksLikeBind, parseBind } from '../utils/bindZone';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

const RecordBulkImportModal = ({ visible, onCancel, onOk, zoneName }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [records, setRecords] = useState([]);
    const [parseError, setParseError] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);

    const handleSubmit = () => {
        form.validateFields().then(values => {
            try {
                const parsed = parseRecords(values.records);
                if (parsed.length === 0) {
                    setParseError(t('No valid records found'));
                    return;
                }

                onOk(parsed);
            } catch (error) {
                setParseError(error.message);
            }
        });
    };

    // 将地理位置字符串（continent/country/province/city/isp/cidr_id）展开为
    // 后端 createRecord 实际读取的数字字段，否则整段 geo 会被静默忽略。
    const parseGeo = (geoStr) => {
        const geo = { continent: 0, country: 0, province: 0, city: 0, isp: 0, cidr_id: 0 };
        if (!geoStr) return geo;
        const parts = String(geoStr).split('/');
        const keys = ['continent', 'country', 'province', 'city', 'isp', 'cidr_id'];
        keys.forEach((key, i) => {
            const val = parseInt((parts[i] || '').trim(), 10);
            if (!Number.isNaN(val)) geo[key] = val;
        });
        return geo;
    };

    const parseRecords = (text) => {
        // BIND zone 文件(从其它 DNS 服务商导出)
        if (looksLikeBind(text)) {
            const { records: bind } = parseBind(text, zoneName);
            return bind.map((r) => ({ ...r, ...parseGeo(''), geo: null }));
        }
        // 尝试CSV解析
        try {
            const result = Papa.parse(text, { header: false, skipEmptyLines: true });

            if (result.errors.length > 0) {
                throw new Error(t('CSV parsing error: {{message}}', { message: result.errors[0].message }));
            }

            return result.data.map(row => {
                if (row.length < 5) {
                    throw new Error(t('Invalid format: each line requires at least 5 fields (name|type|TTL|priority|content)'));
                }

                const [name, type, ttl, priority, content, geo, comment] = row;

                // 验证必填字段
                if (!name || !type || !content) {
                    throw new Error(t('Invalid format: name, type, and content are required'));
                }

                const geoStr = geo ? geo.trim() : '';
                return {
                    name: name.trim(),
                    type: type.trim().toUpperCase(),
                    ttl: parseInt(ttl) || 3600,
                    priority: parseInt(priority) || 0,
                    content: content.trim(),
                    ...parseGeo(geoStr),
                    geo: geoStr || null, // 仅用于预览展示，后端读取的是上面展开的字段
                    comment: comment ? comment.trim() : ''
                };
            });
        } catch (csvError) {
            // 如果CSV解析失败，尝试解析自定义格式
            const lines = text.split('\n').filter(line => line.trim() !== '');

            return lines.map(line => {
                const parts = line.split('|').map(part => part.trim());

                if (parts.length < 5) {
                    throw new Error(t('Invalid format: each line requires at least 5 fields (name|type|TTL|priority|content)'));
                }

                const [name, type, ttl, priority, content, geo, comment] = parts;

                // 验证必填字段
                if (!name || !type || !content) {
                    throw new Error(t('Invalid format: name, type, and content are required'));
                }

                const geoStr = geo || '';
                return {
                    name: name,
                    type: type.toUpperCase(),
                    ttl: parseInt(ttl) || 3600,
                    priority: parseInt(priority) || 0,
                    content: content,
                    ...parseGeo(geoStr),
                    geo: geoStr || null, // 仅用于预览展示，后端读取的是上面展开的字段
                    comment: comment || ''
                };
            });
        }
    };

    const handlePreview = () => {
        form.validateFields().then(values => {
            try {
                const parsed = parseRecords(values.records);
                setRecords(parsed);
                setParseError(null);
                setPreviewVisible(true);
            } catch (error) {
                setParseError(error.message);
            }
        });
    };

    const handleFileUpload = (file) => {
        const reader = new FileReader();
        reader.onload = e => {
            form.setFieldsValue({ records: e.target.result });
        };
        reader.readAsText(file);
        return false;
    };

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            render: (text) => (text === '@' || !text) ? zoneName : `${text}.${zoneName}`
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'TTL',
            dataIndex: 'ttl',
            key: 'ttl',
        },
        {
            title: t('Priority'),
            dataIndex: 'priority',
            key: 'priority',
        },
        {
            title: t('Content'),
            dataIndex: 'content',
            key: 'content',
            ellipsis: true,
        },
        {
            title: t('Geolocation'),
            dataIndex: 'geo',
            key: 'geo',
            render: (text) => text || '-'
        },
        {
            title: t('Notes'),
            dataIndex: 'comment',
            key: 'comment',
            ellipsis: true,
        },
    ];

    return (
        <>
            <Modal
                title={t('Bulk import DNS records')}
                open={visible}
                onCancel={onCancel}
                onOk={handleSubmit}
                width={800}
                okText={t('Import')}
                cancelText={t('Cancel')}
                okButtonProps={{ disabled: parseError !== null }}
            >
                <Form form={form} layout="vertical">
                    <Alert
                        message={t('Bulk import instructions')}
                        description={
                            <div>
                                <Paragraph>
                                    {t('Please enter the records to import in the following format, one record per line:')}
                                </Paragraph>
                                <Paragraph code>
                                    {t('Name|Type|TTL|Priority|Content|Geolocation|Notes')}
                                </Paragraph>
                                <Paragraph>
                                    <ul>
                                        <li>{t('Name, type, and content are required')}</li>
                                        <li>{t('TTL defaults to 3600 seconds')}</li>
                                        <li>{t('Priority applies only to MX and SRV records and defaults to 0')}</li>
                                        <li>{t('Geolocation format: continent/country/province/city/isp/cidr_id')}</li>
                                        <li>{t('Notes are optional')}</li>
                                        <li>{t('A BIND zone file exported from another DNS provider can be pasted or uploaded as is')}</li>
                                    </ul>
                                </Paragraph>
                                <Paragraph>
                                    {t('E.g.:')}<br />
                                    <Text code>{t('www|A|3600|0|192.168.1.1||Web server')}</Text><br />
                                    <Text code>{t('mail|MX|3600|10|mail.example.com||Mail server')}</Text>
                                </Paragraph>
                            </div>
                        }
                        type="info"
                        showIcon
                    />

                    <Divider />

                    <Form.Item name="records" label={t('DNS records')} rules={[{ required: true, message: t('Enter the record value') }]}>
                        <TextArea rows={10} placeholder={t('Enter the DNS records to import')} />
                    </Form.Item>

                    <Form.Item>
                        <Upload beforeUpload={handleFileUpload} showUploadList={false}>
                            <Button icon={<UploadOutlined />}>{t('Import from file')}</Button>
                        </Upload>
                    </Form.Item>

                    {parseError && (
                        <Form.Item>
                            <Alert message={t('Resolution error')} description={parseError} type="error" showIcon />
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Button type="dashed" onClick={handlePreview} icon={<FileExcelOutlined />}>
                            {t('Preview import data')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t('Import preview')}
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={[
                    <Button key="back" onClick={() => setPreviewVisible(false)}>
                        {t('Close')}
                    </Button>,
                ]}
                width={1000}
            >
                <Alert
                    message={t('{{length}} records will be imported into {{zoneName}}', { length: records.length, zoneName: zoneName })}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Table
                    columns={columns}
                    dataSource={records}
                    rowKey={(record, index) => index}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 800 }}
                />
            </Modal>
        </>
    );
};

export default RecordBulkImportModal;
